import { useRef, useState } from "react";
import { toast } from "sonner";
import { Crop, RotateCcw } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { FRAME, FramedImage, frameBox } from "@/components/AdSlot";

const ORIGIN = process.env.REACT_APP_BACKEND_URL;
const VIEWS = [["mobile", "Mobile (carte 16:9)", 320], ["desktop", "Ordinateur (carte 16:7)", 440], ["square", "Bandeau : vignette carrée", 112],
  ["half", "Bandeau : moitié image", 180], ["full", "Bandeau : image en fond", 360]];
const EDIT_BOX = { square: "h-28 w-28 rounded-lg", half: "h-[7.5rem] w-full rounded-lg", full: "h-[7.5rem] w-full rounded-lg" };
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

function Slider({ label, value, min, max, step, onChange, testId }) {
  return (
    <label className="block text-[11px] text-slate-300">{label} <span className="text-slate-500">{value}</span>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="mt-1 w-full accent-[#B7F52A]" data-testid={testId} />
    </label>
  );
}

function Frame({ src, view, width, f, setF, active, onPick, cid }) {
  const drag = useRef(null);
  const down = (e) => { onPick(); drag.current = { px: e.clientX, py: e.clientY, x: f.x, y: f.y }; e.currentTarget.setPointerCapture(e.pointerId); };
  const move = (e) => {
    const d = drag.current;
    if (!d) return;
    const r = e.currentTarget.getBoundingClientRect();
    setF({ ...f, x: clamp(d.x - ((e.clientX - d.px) * 100) / (r.width * f.zoom), 0, 100), y: clamp(d.y - ((e.clientY - d.py) * 100) / (r.height * f.zoom), 0, 100) });
  };
  return (
    <div style={{ width, maxWidth: "100%" }}>
      <div onPointerDown={down} onPointerMove={move} onPointerUp={() => { drag.current = null; }} className={`cursor-grab touch-none rounded-lg ring-2 ${active ? "ring-[#B7F52A]" : "ring-white/10"}`} data-testid={`fit-frame-${view}-${cid}`}>
        <FramedImage src={src} alt="" frame={f} className={EDIT_BOX[view] || `${frameBox(view)} rounded-lg`} />
      </div>
    </div>
  );
}

export default function CreativeFit({ c, load }) {
  const [open, setOpen] = useState(false);
  const [fit, setFit] = useState(() => Object.fromEntries(VIEWS.map(([k]) => [k, { ...FRAME, ...c.image_fit?.[k] }])));
  const [view, setView] = useState("mobile");
  if (!c.image_version) return null;
  const src = `${ORIGIN}/api/ads/creative-image/${c.id}?v=${c.image_version}`;
  const f = fit[view];
  const set = (k) => (v) => setFit({ ...fit, [view]: { ...f, [k]: v } });
  const save = async () => {
    try { await api.put(`/superadmin/regie/creatives/${c.id}/fit`, fit); toast.success("Cadrage enregistré"); load(); setOpen(false); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!open) return <button onClick={() => setOpen(true)} className="mpl-btn-ghost px-2 py-1 text-xs" data-testid={`creative-fit-${c.id}`}><Crop size={12} /> Cadrer l'image</button>;
  return (
    <div className="basis-full space-y-3 rounded-xl border border-white/10 bg-black/20 p-3" data-testid={`creative-fit-editor-${c.id}`}>
      <p className="text-[11px] text-slate-400">Fais glisser l'image dans chaque encart pour la déplacer, puis règle le zoom. Chaque format a son propre cadrage : cartes mobile et ordinateur, et les trois mises en page des bandeaux compacts (sous le classement, défis, bannière, récap).</p>
      <div className="flex flex-wrap items-start gap-4">
        {VIEWS.map(([k, label, w]) => (
          <div key={k} className="min-w-0 max-w-full space-y-1" style={{ width: w }}>
            <button onClick={() => setView(k)} className={`text-[11px] font-semibold ${view === k ? "text-[#B7F52A]" : "text-slate-400"}`} data-testid={`fit-view-${k}-${c.id}`}>{label}</button>
            <Frame src={src} view={k} width={w} f={fit[k]} setF={(v) => setFit((x) => ({ ...x, [k]: v }))} active={view === k} onPick={() => setView(k)} cid={c.id} />
          </div>
        ))}
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <Slider label="Zoom" value={f.zoom} min={1} max={3} step={0.05} onChange={set("zoom")} testId={`fit-zoom-${c.id}`} />
        <Slider label="Gauche / droite" value={Math.round(f.x)} min={0} max={100} step={1} onChange={set("x")} testId={`fit-x-${c.id}`} />
        <Slider label="Haut / bas" value={Math.round(f.y)} min={0} max={100} step={1} onChange={set("y")} testId={`fit-y-${c.id}`} />
      </div>
      <p className="text-[11px] text-slate-500">Astuce : si un déplacement ne change rien, augmente un peu le zoom pour avoir de la marge.</p>
      <div className="flex flex-wrap gap-2">
        <button onClick={save} className="mpl-btn-primary px-3 py-1.5 text-xs" data-testid={`fit-save-${c.id}`}>Enregistrer le cadrage</button>
        <button onClick={() => setFit({ ...fit, [view]: { ...FRAME } })} className="mpl-btn-ghost px-3 py-1.5 text-xs" data-testid={`fit-reset-${c.id}`}><RotateCcw size={12} /> Réinitialiser ce format</button>
        <button onClick={() => setOpen(false)} className="mpl-btn-ghost px-3 py-1.5 text-xs" data-testid={`fit-cancel-${c.id}`}>Fermer</button>
      </div>
    </div>
  );
}
